import { z } from 'zod';
import { UpdateExperimentSettingsFormFieldNames } from 'modules/experiments/constants/updateExperimentSettings';
import {
  MIN_HOLDOUT_GROUP_SIZE,
  MAX_HOLDOUT_GROUP_SIZE,
  MIN_ATTRIBUTION_PERIOD_HOURS,
  MAX_ATTRIBUTION_PERIOD_HOURS,
  MIN_TEST_SIZE_PERCENTAGE,
  MAX_TEST_SIZE_PERCENTAGE,
  MIN_TEST_DURATION_HOURS,
  MAX_TEST_DURATION_HOURS,
  MIN_TEST_DURATION_MINUTES,
  MAX_TEST_DURATION_MINUTES,
  MIN_SENDS_PER_VARIANT,
} from 'modules/experiments/takeover/constants';
import { SystemConversionsEventType, TestStrategy } from 'modules/experiments/types';
import { WEEK_IN_MINUTES } from '../constants';

const {
  HOLDOUT_GROUP_SIZE,
  ATTRIBUTION_PERIOD_HOURS,
  HOLDOUT_TEST_SIZE_PERCENTAGE,
  TEST_DURATION_HOURS,
  TEST_DURATION_MINUTES,
  HAS_HOLDOUT_GROUP,
  WINNING_CRITERIA_NAME,
  TEST_SENDS_PER_VARIATION,
  TEST_STRATEGY,
  TRIGGERED_TEST_STRATEGY,
} = UpdateExperimentSettingsFormFieldNames;

const GROUP_SIZE_RANGE_ERROR_TEXT = 'This must be a value between 0.01 and 99.99';

const systemEvents: string[] = Object.values(SystemConversionsEventType);

const TEST_SENDS_PER_VARIATION_RULE = z
  .number({
    invalid_type_error: 'This must be a number greater than 1',
  })
  .gt(MIN_SENDS_PER_VARIANT, 'This must be a number greater than 1');

const HOLDOUT_GROUP_SIZE_RULE = z
  .number({
    invalid_type_error: GROUP_SIZE_RANGE_ERROR_TEXT,
  })
  .gte(MIN_HOLDOUT_GROUP_SIZE, GROUP_SIZE_RANGE_ERROR_TEXT)
  .lte(MAX_HOLDOUT_GROUP_SIZE, GROUP_SIZE_RANGE_ERROR_TEXT);

const ATTRIBUTION_HOURS_RULE = z
  .number({
    invalid_type_error: `Please use a time period greater than ${MIN_ATTRIBUTION_PERIOD_HOURS} and less than ${MAX_ATTRIBUTION_PERIOD_HOURS} hours`,
  })
  .gte(
    MIN_ATTRIBUTION_PERIOD_HOURS,
    `Please use a time period greater than ${MIN_ATTRIBUTION_PERIOD_HOURS} and less than ${MAX_ATTRIBUTION_PERIOD_HOURS} hours`,
  )
  .lte(
    MAX_ATTRIBUTION_PERIOD_HOURS,
    `Please use a time period greater than ${MIN_ATTRIBUTION_PERIOD_HOURS} and less than ${MAX_ATTRIBUTION_PERIOD_HOURS} hours`,
  );

const HOLDOUT_TEST_SIZE_PERCENTAGE_RULE = z
  .number({
    invalid_type_error: GROUP_SIZE_RANGE_ERROR_TEXT,
  })
  .gte(MIN_TEST_SIZE_PERCENTAGE, GROUP_SIZE_RANGE_ERROR_TEXT)
  .lte(MAX_TEST_SIZE_PERCENTAGE, GROUP_SIZE_RANGE_ERROR_TEXT);

const HAS_HOLDOUT_GROUP_RULE = z.boolean().optional();

const WINNING_CRITERIA_NAME_RULE = z.string().optional();

const TEST_DURATION_HOURS_RULE = z
  .number({
    invalid_type_error: 'This must be a number between 1 and 169',
  })
  .gte(MIN_TEST_DURATION_HOURS, 'This must be a number between 1 and 169')
  .lte(MAX_TEST_DURATION_HOURS, 'This must be a number between 1 and 169');

const TEST_DURATION_MINUTES_RULE = z
  .number({
    invalid_type_error: 'This must be a number between 1 and 59',
  })
  .gte(MIN_TEST_DURATION_MINUTES, 'This must be a number between 1 and 59')
  .lte(MAX_TEST_DURATION_MINUTES, 'This must be a number between 1 and 59');

const TEST_STRATEGY_RULE = z.string().optional();
const TRIGGERED_TEST_STRATEGY_RULE = z.string().optional();

export const EDIT_EXPERIMENT_SETTINGS_SCHEMA = z
  .object({
    [HOLDOUT_GROUP_SIZE]: HOLDOUT_GROUP_SIZE_RULE,
    [ATTRIBUTION_PERIOD_HOURS]: ATTRIBUTION_HOURS_RULE,
    [HOLDOUT_TEST_SIZE_PERCENTAGE]: HOLDOUT_TEST_SIZE_PERCENTAGE_RULE,
    [HAS_HOLDOUT_GROUP]: HAS_HOLDOUT_GROUP_RULE,
    [WINNING_CRITERIA_NAME]: WINNING_CRITERIA_NAME_RULE,
    [TEST_DURATION_HOURS]: TEST_DURATION_HOURS_RULE,
    [TEST_DURATION_MINUTES]: TEST_DURATION_MINUTES_RULE,
    [TEST_SENDS_PER_VARIATION]: TEST_SENDS_PER_VARIATION_RULE,
    [TEST_STRATEGY]: TEST_STRATEGY_RULE,
    [TRIGGERED_TEST_STRATEGY]: TRIGGERED_TEST_STRATEGY_RULE,
  })
  .superRefine(
    (
      {
        hasHoldOutGroup,
        holdOutGroupSize,
        testSizePercentage,
        winningCriteriaName,
        testDurationHours,
        testDurationMinutes,
        testStrategy,
      },
      ctx,
    ) => {
      if (
        !!holdOutGroupSize &&
        !!testSizePercentage &&
        hasHoldOutGroup &&
        testStrategy === TestStrategy.SUBSET_USERS
      ) {
        const total = holdOutGroupSize + testSizePercentage;
        if (total >= 100) {
          // adding separate issues to target different paths will target errors for two inputs.
          // [HOLDOUT_GROUP_SIZE, HOLDOUT_TEST_SIZE_PERCENTAGE] nests will show up as nested fields
          ctx.addIssue({
            code: 'custom',
            message: 'The sum of holdout and test group must be less than 100%',
            path: [HOLDOUT_GROUP_SIZE],
          });
          ctx.addIssue({
            code: 'custom',
            message: 'The sum of holdout and test group must be less than 100%',
            path: [HOLDOUT_TEST_SIZE_PERCENTAGE],
          });
        }
      }

      if (winningCriteriaName) {
        if (hasHoldOutGroup && systemEvents.includes(winningCriteriaName)) {
          ctx.addIssue({
            code: 'custom',
            message: 'You must select a custom event since you added a holdout',
            path: [WINNING_CRITERIA_NAME],
          });
        }
      }

      if (
        (testStrategy === TestStrategy.SUBSET_USERS &&
          testDurationHours === 0 &&
          testDurationMinutes === 0) ||
        testDurationHours * 60 + testDurationMinutes > WEEK_IN_MINUTES
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Total run time must be between 1 and 168 hours',
          path: [TEST_DURATION_HOURS],
        });
        ctx.addIssue({
          code: 'custom',
          message: 'Total run time must be between 1 and 168 hours',
          path: [TEST_DURATION_MINUTES],
        });
      }
    },
  );
